// frontend/src/content/blog/blogData.js
//
// Public API for the Leadify blog. Each article is structured DATA (not
// markdown/HTML), rendered by src/components/blog/ArticleBody.js into the site's
// design language - the same content-as-data pattern as faqData.js. This keeps
// the blog dependency-free (no react-markdown), fully styled, and present in the
// prerendered HTML for AI crawlers.
//
// THE DATA ITSELF LIVES IN TWO FILES, and this module composes them:
//   - blogIndex.js   LIGHT metadata (slug/image/title/meta/dates/tags/excerpt)
//   - blogBodies.js  HEAVY content keyed by slug (tldr/blocks/faq/cta)
// Each field has exactly ONE home; `articles` below joins them, so every
// existing consumer (Blog.js, BlogArticle.js, getArticle, articleSlugs,
// sortArticles, seoContentSync.test.js, the prerender) is unaffected.
//
// WHY THE SPLIT: importing THIS module pulls in the article bodies. That is fine
// for /blog and /blog/:slug (lazy routes), but NOT for anything the eagerly
// imported Homepage reaches - see the header of blogIndex.js. If you need blog
// metadata from an eagerly-reachable module, import blogIndex.js directly.
//
// Hebrew is the only blog language for now (Hebrew-first market); English
// versions can be added later as a parallel field.
//
// Inline markup inside any text string is parsed by
// src/components/shared/inlineMarkup.js:
//   **bold**              -> <strong>
//   [label](/path)        -> internal RouterLink
//   [label](https://...)  -> external <a target="_blank">
//   "\n"                   -> line break within a paragraph
//
// Block types (see ArticleBody.js): h2 | h3 | p | ul | ol | table | quote | note
//
// IMPORTANT: when you add/remove an article, also update:
//   - frontend/scripts/prerender.js  (ROUTES - add /blog/<slug>)
//   - frontend/public/sitemap.xml    (add the <url> entry)

import { articleIndex, sortArticleList, DEFAULT_SORT } from "./blogIndex";
import { articleBodies } from "./blogBodies";

// Re-exported so this module's public API is unchanged by the split: callers
// keep importing everything blog-related from blogData.
export {
  BLOG_BASE,
  BLOG_AUTHOR,
  formatHebrewDate,
  articleIndex,
  sortArticleList,
  sortArticleIndex,
  normalizeSort,
  titleCollator,
  SORT_NEWEST,
  SORT_OLDEST,
  SORT_ALPHA,
  SORT_OPTIONS,
  DEFAULT_SORT,
} from "./blogIndex";

/**
 * Every article, fully composed - the shape consumers have always seen.
 *
 * Index order is preserved (Array.map), so authored order - and therefore the
 * tie-break behaviour the sort relies on - is identical to before the split.
 * A missing body would silently yield an article with no blocks, so the slug
 * sets are checked in the build (scripts/checkBlogSplit.js) and in tests.
 */
export const articles = articleIndex.map((meta) => ({
  ...meta,
  ...articleBodies[meta.slug],
}));

export const articleSlugs = articles.map((a) => a.slug);

export function getArticle(slug) {
  return articles.find((a) => a.slug === slug) || null;
}

/**
 * Return a NEW array of articles in the requested order.
 *
 * Never sorts in place: `articles` is module-level shared state that other
 * consumers (JSON-LD, getRelatedArticles, articleSlugs) read, and mutating it
 * would reorder them as a side effect.
 *
 * TIE-BREAK: dates are coarse (whole days) and 10 of the 11 articles currently
 * share a date with at least one other — 6 alone sit on 2026-06-30. Neither date
 * comparator breaks those ties, so their relative order is decided by
 * Array.prototype.sort's STABILITY, which ES2019 guarantees: tied articles keep
 * their authored (array) order. That is a deliberate, specified reliance, not an
 * accident — it is what makes the default /blog order deterministic and so
 * reproducible in the prerendered HTML. If you ever need an explicit tie-break,
 * add it to the comparator rather than reordering the array.
 *
 * @param {string} [sort] A SORT_OPTIONS value; anything else uses DEFAULT_SORT.
 * @returns {Array<object>} Sorted copy.
 */
export function sortArticles(sort = DEFAULT_SORT) {
  return sortArticleList(articles, sort);
}

// Up to `limit` other articles, ranked by shared tags with the given slug
// (ties keep array order; fills with the rest if too few share a tag). Powers
// the per-article "מאמרים קשורים" block.
export function getRelatedArticles(slug, limit = 3) {
  const current = getArticle(slug);
  const others = articles.filter((a) => a.slug !== slug);
  if (!current) return others.slice(0, limit);
  const tagSet = new Set(current.tags || []);
  return others
    .map((a) => ({ a, shared: (a.tags || []).filter((t) => tagSet.has(t)).length }))
    .sort((x, y) => y.shared - x.shared)
    .slice(0, limit)
    .map((s) => s.a);
}
